import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    model,
    ViewEncapsulation
} from "@angular/core";
import { LgIconComponent } from "../lg-icon/lg-icon.component";
import { LgTranslatePipe } from "@logex/framework/lg-localization";

/**
 * Paginator navigation controls component.
 *
 * Renders first/previous/page-selector/next/last navigation buttons.
 *
 * @example
 * ```html
 * <lg-paginator
 *   [page]="currentPage()"
 *   (pageChange)="goToPage($event)"
 *   [totalPages]="totalPages()"
 *   [loading]="isLoading()"
 * />
 * ```
 */
@Component({
    selector: "lg-paginator",
    standalone: true,
    imports: [LgIconComponent, LgTranslatePipe],
    templateUrl: "./lg-paginator.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class LgPaginatorComponent {
    /** Current page (0-based). Two-way bindable. */
    readonly page = model.required<number>();

    /** Total number of pages. */
    readonly totalPages = input.required<number>();

    /** When true, all controls are disabled. */
    readonly loading = input<boolean>(false);

    /**
     * Maximum number of pages for which a dropdown is shown.
     * Above this threshold an input field is rendered instead.
     * Set to 0 to always use the input field.
     *
     * @default 10
     */
    readonly dropdownPageThreshold = input<number>(10);

    protected readonly isFirstPage = computed(() => this.page() === 0);
    protected readonly isLastPage = computed(() => this.page() >= this.totalPages() - 1);

    protected readonly useInputField = computed(() => {
        const threshold = this.dropdownPageThreshold();
        return threshold === 0 || this.totalPages() > threshold;
    });

    protected readonly pageNumbers = computed(() =>
        Array.from({ length: this.totalPages() }, (_, i) => i + 1)
    );

    protected goToFirstPage(): void {
        if (!this.isFirstPage() && !this.loading()) {
            this.page.set(0);
        }
    }

    protected goToPreviousPage(): void {
        if (!this.isFirstPage() && !this.loading()) {
            this.page.update(p => p - 1);
        }
    }

    protected goToNextPage(): void {
        if (!this.isLastPage() && !this.loading()) {
            this.page.update(p => p + 1);
        }
    }

    protected goToLastPage(): void {
        if (!this.isLastPage() && !this.loading()) {
            this.page.set(this.totalPages() - 1);
        }
    }

    protected goToPage(page: number): void {
        if (this.loading()) {
            return;
        }
        this.page.set(Math.max(0, Math.min(page, this.totalPages() - 1)));
    }

    protected onPageSelectChange(event: Event): void {
        const select = event.target as HTMLSelectElement;
        const pageNumber = parseInt(select.value, 10);
        if (!isNaN(pageNumber) && Number.isInteger(pageNumber)) {
            const total = this.totalPages();
            if (pageNumber >= 1 && pageNumber <= total) {
                this.goToPage(pageNumber - 1);
            }
        }
    }

    protected onPageInputEnter(event: Event): void {
        const el = event.target as HTMLInputElement;
        const pageNumber = parseInt(el.value, 10);
        if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= this.totalPages()) {
            this.goToPage(pageNumber - 1);
        } else {
            el.value = (this.page() + 1).toString();
        }
    }

    protected onPageInputBlur(event: Event): void {
        const el = event.target as HTMLInputElement;
        const pageNumber = parseInt(el.value, 10);
        if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > this.totalPages()) {
            el.value = (this.page() + 1).toString();
        }
    }
}
